import firebase from "firebase/app";
import "firebase/auth";
import "firebase/database";
import bcrypt from "bcryptjs";
import aes from "crypto-js/aes";
import CryptoJS from "crypto-js";

export const PASSWORDS_API_ENDPOINT =
  process.env.NODE_ENV === "production"
    ? "/api/passwords"
    : "http://localhost:7777/api/passwords";

export const NOTES_API_ENDPOINT =
  process.env.NODE_ENV === "production"
    ? "/api/notes"
    : "http://localhost:7777/api/notes";

export const PIN_INIT_API_ENDPOINT =
  process.env.NODE_ENV === "production"
    ? "/api/pin"
    : "http://localhost:7777/api/pin";

const CSE_ENABLED_API_ENDPOINT =
  process.env.NODE_ENV === "production"
    ? "/api/cse"
    : "http://localhost:7777/api/cse";

const HOHOPIN_VALIDATION_API_ENDPOINT =
  process.env.NODE_ENV === "production"
    ? "/api/pin/validation"
    : "http://localhost:7777/api/pin/validation";

const CSE_SALT_API_ENDPOINT =
  process.env.NODE_ENV === "production"
    ? "/api/cse/salt"
    : "http://localhost:7777/api/cse/salt";

export const globalFunctions = {
  getIdToken: async function() {
    let idToken = await firebase
      .auth()
      .currentUser.getIdToken(false)
      .then(function(idToken) {
        return idToken;
      })
      .catch(function(error) {
        // Handle error
        throw `Failed while fetching the idToken from the firebase: ${error.meesage}`;
      });

    return idToken;
  },
  fetchPasswords: async function() {
    let cseEnabled = await this.isCSEEnabled();
    if (cseEnabled) {
      let correctHops = await this.validateHops(this.getHops());
      if (!correctHops) {
        this.$router.push("/pin/enter");
        return;
      }
    }
    let passwords = [];
    let idToken = await this.getIdToken();
    const options = {
      method: "GET",
      headers: {
        Authorization: idToken
      }
    };
    let serverResponse = await fetch(PASSWORDS_API_ENDPOINT, options)
      .then(res => {
        return res.json();
      })
      .catch(err => {
        alert(`Failed while fetching the passwords: ${err}`);
        return;
      });
    if (serverResponse.success) {
      serverResponse.data.forEach(element => {
        passwords.push(
          new SecretPasswordObject(
            element.guid,
            cseEnabled ? this.decrypt(element.content) : element.content,
            cseEnabled ? this.decrypt(element.username) : element.username,
            cseEnabled ? this.decrypt(element.password) : element.password,
            cseEnabled ? this.decrypt(element.website) : element.website,
            cseEnabled
              ? this.decrypt(element.isFavorite) === "true"
              : element.isFavorite,
            cseEnabled ? this.decrypt(element.notes) : element.notes
          )
        );
      });

      if (cseEnabled) {
        // Sort passwords
        // Filter favorite passwords
        let favoritePasswords = passwords.filter(pass => pass.isFavorite);
        let nonFavoritePasswords = passwords.filter(pass => !pass.isFavorite);

        // Sort alphabetically
        favoritePasswords.sort(function(a, b) {
          return a.content.toLowerCase() < b.content.toLowerCase() ? -1 : 1;
        });
        nonFavoritePasswords.sort(function(a, b) {
          return a.content.toLowerCase() < b.content.toLowerCase() ? -1 : 1;
        });

        // Combine all passwords into single array
        passwords = favoritePasswords.concat(nonFavoritePasswords);
      }

      return passwords;
    } else {
      alert(`Failed while fetching the passwords: ${serverResponse.data}`);
    }
    return passwords;
  },
  fetchNotes: async function() {
    let cseEnabled = await this.isCSEEnabled();
    if (cseEnabled) {
      let correctHops = await this.validateHops(this.getHops());
      if (!correctHops) {
        this.$router.push("/pin/enter");
        return;
      }
    }
    let notes = [];
    let idToken = await this.getIdToken();
    const options = {
      method: "GET",
      headers: {
        Authorization: idToken
      }
    };
    let serverResponse = await fetch(NOTES_API_ENDPOINT, options).then(res => {
      return res.json();
    });
    if (serverResponse.success) {
      serverResponse.data.forEach(element => {
        notes.push(
          new SecretNoteObject(
            cseEnabled
              ? this.decrypt(element.noteContent)
              : element.noteContent,
            cseEnabled
              ? this.decrypt(element.lastEditedDate)
              : element.lastEditedDate,
            element.guid,
            cseEnabled ? this.decrypt(element.timeStamp) : element.timeStamp,
            cseEnabled
              ? this.decrypt(element.isFavorite) === "true"
              : element.isFavorite
          )
        );
      });

      if (cseEnabled) {
        // Sort notes
        let favoriteNotes = notes.filter(note => note.isFavorite);
        let nonFavoriteNotes = notes.filter(note => !note.isFavorite);

        // Sort by date
        favoriteNotes.sort(function(a, b) {
          return b.timeStamp - a.timeStamp;
        });
        nonFavoriteNotes.sort(function(a, b) {
          return b.timeStamp - a.timeStamp;
        });

        // Combine all notes into single array
        notes = favoriteNotes.concat(nonFavoriteNotes);
      }
    } else {
      alert(`Failed while fetching the notes: ${serverResponse.data}`);
    }
    return notes;
  },
  isCSEEnabled: async function() {
    let idToken = await this.getIdToken();
    const options = {
      method: "GET",
      headers: {
        Authorization: idToken
      }
    };
    let serverResponse = await fetch(CSE_ENABLED_API_ENDPOINT, options)
      .then(res => {
        return res.json();
      })
      .catch(err => {
        alert(`Failed while fetching the cse endpoint: ${err}`);
        return;
      });

    if (!serverResponse.success) {
      throw "failed to fetch data from server";
    }

    return serverResponse.data;
  },
  validateHops: async function(hops) {
    if (hops == null || hops == undefined) {
      hops = this.getHops();
    }

    let idToken = await this.getIdToken();
    let salt = await this.fetchSalt();

    let options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: idToken
      },
      body: JSON.stringify({
        hohoPin: this.calculateHash(hops, salt)
      })
    };
    let serverResponse = await fetch(HOHOPIN_VALIDATION_API_ENDPOINT, options)
      .then(res => {
        return res.json();
      })
      .catch(err => {
        alert(`Failed while fetching the pin/validate endpoint: ${err}`);
        return;
      });

    if (serverResponse.success) {
      return serverResponse.data;
    }
    throw "failed to fetch data from server";
  },
  fetchSalt: async function() {
    let idToken = await this.getIdToken();
    let options = {
      method: "GET",
      headers: {
        Authorization: idToken
      }
    };
    // Fetch salt from the server
    let salt = "";
    let serverResponse = await fetch(CSE_SALT_API_ENDPOINT, options)
      .then(res => {
        return res.json();
      })
      .catch(err => {
        alert(`Failed while fetching the cse/salt endpoint: ${err}`);
        return;
      });

    if (!serverResponse.success) {
      throw "failed to fetch salt data from server";
    } else if (!serverResponse.data || serverResponse.data == "") {
      throw "failed to fetch salt from server, salt does not exist";
    } else {
      salt = serverResponse.data;
    }

    return salt;
  },
  generateSalt: function() {
    return bcrypt.genSaltSync(10);
  },
  calculateHash: function(value, salt) {
    try {
      if (value == null || value == undefined) value = "";
      if (salt == null || salt == undefined) salt = "";
      value = value.toString();
      salt = salt.toString();
      return bcrypt.hashSync(value, salt);
    } catch (error) {
      throw `${error}`;
    }
  },
  encrypt: function(data, encryptionKey) {
    if (encryptionKey == null || encryptionKey == undefined)
      encryptionKey = this.getHops();
    if (data == null || data == undefined) data = "";
    try {
      data = data.toString();
      return aes.encrypt(data, encryptionKey).toString();
    } catch (error) {
      throw `encryption error: ${error}`;
    }
  },
  decrypt: function(data, encryptionKey) {
    if (encryptionKey == null || encryptionKey == undefined)
      encryptionKey = this.getHops();
    if (data == null || data == undefined) data = "";
    try {
      data = data.toString();
      return aes.decrypt(data, encryptionKey).toString(CryptoJS.enc.Utf8);
    } catch (error) {
      throw `decryption error: ${error}`;
    }
  },
  setHops: function(hops) {
    // Set the hash of (pin+salt)
    localStorage.userHops = hops;
  },
  clearHops: function() {
    localStorage.removeItem("userHops");
  },
  getHops: function() {
    return localStorage.getItem("userHops");
  }
};

export class SecretPasswordObject {
  constructor(guid, content, username, password, website, isFavorite, notes) {
    this.guid = guid;
    this.content = content;
    this.username = username;
    this.password = password;
    this.website = website;
    this.revealPassword = false;
    this.isFavorite = isFavorite;
    this.notes = notes;
  }
}

export class SecretNoteObject {
  constructor(noteContent, lastEditedDate, guid, timeStamp, isFavorite) {
    this.noteContent = noteContent;
    this.lastEditedDate = lastEditedDate;
    this.guid = guid;
    this.timeStamp = timeStamp;
    this.isFavorite = isFavorite;
  }
}
